import {dropbox_os} from '@dropbox/api-v2-client';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {DropboxClientContext} from 'App';
import {snackbarAtom} from 'atoms/snackbar';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {useCallback, useContext} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';

import {getSitePublicationState} from './api';
import {appPublishSuccessSnackbar} from './appPublishSnackbar';
import {displayName} from './appUtils';
import {AppPublishResult} from './publishApp';
import {UploadDropzone} from './UploadDropzone';

interface UpdateAppLocationState {
  app?: dropbox_os.PublishedApp;
}

export const UpdateApp: React.FC = () => {
  const client = useContext(DropboxClientContext);
  const navigate = useNavigate();
  const setSnackbar = useSetAtom(snackbarAtom);
  const {siteId = ''} = useParams();
  const {state} = useLocation() as {state: UpdateAppLocationState | null};
  const app = state?.app;
  const publicationStateQuery = useQuery({
    queryKey: ['apps', 'publication-state', siteId, client],
    queryFn: () => {
      if (!client) {
        throw new Error('Dropbox client is unavailable');
      }
      return getSitePublicationState(client, {site_id: siteId});
    },
    enabled: Boolean(client && siteId),
  });
  const publicationState = publicationStateQuery.data;
  const slug = publicationState?.slug || (app?.id === siteId ? app.slug : undefined);
  const name = slug ? displayName(slug) : null;
  const title = name ? t('apps_update_title', {name}) : t('apps_update_title_fallback');
  const existingSite =
    publicationState?.site_id === siteId &&
    publicationState.slug &&
    typeof publicationState.current_candidate_deployment_id === 'string'
      ? {
          expectedCandidateDeploymentId: publicationState.current_candidate_deployment_id,
          siteId,
          slug: publicationState.slug,
          spaFallbackEnabled: Boolean(publicationState.spa_fallback_enabled),
        }
      : null;
  useDocumentTitle(title);

  const handlePublishSuccess = useCallback(
    (result: AppPublishResult) => {
      setSnackbar(appPublishSuccessSnackbar(result));
      navigate(ROUTE_PATHS.APPS);
    },
    [navigate, setSnackbar]
  );

  return (
    <Layout.Container
      breadcrumb={[
        {children: t('apps'), to: ROUTE_PATHS.APPS},
        {
          children: title,
          to: ROUTE_PATHS.APPS_UPDATE.replace(':siteId', siteId),
        },
      ]}
    >
      <Stack gap="8">
        <Title size={24}>{title}</Title>
        <Text color="subtle">
          {slug ? t('apps_update_description', {slug}) : t('apps_update_description_fallback')}
        </Text>
      </Stack>

      <Box paddingTop="24">
        {!client || publicationStateQuery.isPending ? (
          <Text role="status" color="subtle">
            {t('apps_update_loading')}
          </Text>
        ) : publicationStateQuery.isError || !existingSite ? (
          <Text role="alert" color="subtle">
            {t('apps_update_load_error')}
          </Text>
        ) : (
          <UploadDropzone existingSite={existingSite} onPublishSuccess={handlePublishSuccess} />
        )}
      </Box>
    </Layout.Container>
  );
};
